import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  AlertTriangle, CheckCircle2, Clock, Package, Upload,
  FileText, Shield, TrendingUp, ArrowRight
} from 'lucide-react';

function KPICard({ label, value, icon: Icon, color = 'text-slate-600', onClick }) {
  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-shadow ${onClick ? '' : 'cursor-default'}`}
      onClick={onClick}
      data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-slate-100 ${color}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500 uppercase tracking-wider font-medium">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function WorkflowTracker({ stages, packages, contractFamily }) {
  const stagePackageCounts = {};
  (packages || []).forEach(p => {
    const s = p.package_stage || 'draft';
    stagePackageCounts[s] = (stagePackageCounts[s] || 0) + 1;
  });

  return (
    <Card data-testid="workflow-tracker">
      <CardContent className="p-4">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">
          Contract Workflow — {contractFamily}
        </h3>
        <div className="flex items-center gap-1 overflow-x-auto pb-2">
          {(stages || []).map((stage, idx) => {
            const count = stagePackageCounts[stage.key] || 0;
            const isActive = count > 0;
            return (
              <React.Fragment key={stage.key}>
                {idx > 0 && <ArrowRight className="h-3 w-3 text-slate-300 flex-shrink-0" />}
                <div
                  className={`flex-shrink-0 px-3 py-2 rounded text-center min-w-[100px] border ${
                    isActive
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-slate-50 text-slate-400 border-slate-200'
                  }`}
                >
                  <p className="text-[10px] font-medium uppercase leading-tight">{stage.label}</p>
                  {isActive && (
                    <p className="text-lg font-bold mt-0.5">{count}</p>
                  )}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function BlockerPanel({ defectSummary, onNavigate }) {
  const byStatus = defectSummary?.by_status || {};
  const bySeverity = defectSummary?.by_severity || {};

  return (
    <Card className="border-amber-200 bg-amber-50" data-testid="blocker-panel">
      <CardContent className="p-4">
        <h3 className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-3 flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5" /> Completion Blockers
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="text-center">
            <p className="text-xl font-bold text-red-600">{bySeverity.blocker || 0}</p>
            <p className="text-[10px] text-slate-500 uppercase">Blocking</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-orange-600">{bySeverity.major || 0}</p>
            <p className="text-[10px] text-slate-500 uppercase">Major</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-yellow-600">{bySeverity.minor || 0}</p>
            <p className="text-[10px] text-slate-500 uppercase">Minor</p>
          </div>
          <div className="text-center">
            <p className="text-xl font-bold text-slate-500">{bySeverity.observation || 0}</p>
            <p className="text-[10px] text-slate-500 uppercase">Observation</p>
          </div>
        </div>
        <div className="mt-3 flex gap-2 flex-wrap">
          {Object.entries(byStatus).filter(([k]) => !['closed','waived','deferred'].includes(k)).map(([status, count]) => (
            <Badge key={status} variant="outline" className="text-[10px] border-amber-300 text-amber-800">
              {status.replace(/_/g, ' ')}: {count}
            </Badge>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export default function CloseoutDashboard({ projectId, settings, onNavigate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, [projectId]);

  const loadDashboard = async () => {
    try {
      const res = await api.closeout.getDashboard(projectId);
      setData(res.data);
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;
  }

  const kpi = data?.kpi || {};
  const stages = data?.stage_labels ? Object.entries(data.stage_labels).map(([key, label]) => ({ key, label })) : [];

  return (
    <div className="space-y-5" data-testid="closeout-dashboard">
      <h2 className="text-lg font-barlow font-bold uppercase tracking-tight">Dashboard</h2>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
        <KPICard label="Open Defects" value={kpi.open_defects || 0} icon={AlertTriangle} color="text-red-500" onClick={() => onNavigate('defects')} />
        <KPICard label="Blockers" value={kpi.completion_blockers || 0} icon={Shield} color="text-orange-500" />
        <KPICard label="Minor Outstanding" value={kpi.minor_outstanding || 0} icon={Clock} color="text-yellow-500" />
        <KPICard label="Packages" value={kpi.total_packages || 0} icon={Package} color="text-blue-500" onClick={() => onNavigate('packages')} />
        <KPICard label="Uploads Pending" value={kpi.consultant_uploads_pending || 0} icon={Upload} color="text-purple-500" onClick={() => onNavigate('uploads')} />
      </div>

      {/* Workflow Tracker */}
      <WorkflowTracker
        stages={stages}
        packages={data?.packages || []}
        contractFamily={data?.contract_family || 'JBCC'}
      />

      {/* Blocker Panel */}
      {(data?.defect_summary?.total || 0) > 0 && (
        <BlockerPanel defectSummary={data?.defect_summary} onNavigate={onNavigate} />
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Packages Ready</h4>
            <p className="text-3xl font-bold text-green-600">{kpi.packages_ready || 0}</p>
            <p className="text-xs text-slate-400 mt-1">for {settings?.profile?.completion_term || 'completion'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Completed</h4>
            <p className="text-3xl font-bold text-slate-900">{kpi.packages_completed || 0}</p>
            <p className="text-xs text-slate-400 mt-1">handed over / closed</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Post-Handover</h4>
            <p className="text-3xl font-bold text-blue-600">{kpi.packages_post_handover || 0}</p>
            <p className="text-xs text-slate-400 mt-1">{settings?.profile?.post_completion_term || 'defect correction period'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Defects by Trade */}
      {data?.defect_summary?.by_trade && Object.keys(data.defect_summary.by_trade).length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Defects by Trade</h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.defect_summary.by_trade)
                .sort((a, b) => b[1] - a[1])
                .map(([trade, count]) => (
                  <Badge key={trade} variant="outline" className="text-xs">
                    {trade.replace(/_/g, ' ')}: {count}
                  </Badge>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
